import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { communicationsAPI } from "@/lib/communications-api";
import type { CommunicationListParams } from "@/lib/communications-api";
import { queryKeys } from "@/lib/query-keys";
import { useToast } from "@/hooks/use-toast";

export const useCommunicationList = (params: CommunicationListParams) => {
  return useQuery({
    queryKey: queryKeys.communications.list(params),
    queryFn: () => communicationsAPI.listCommunications(params),
    enabled: !!params.user_id,
    retry: false,
  });
};

export const useCommunicationStats = (userId: string) => {
  return useQuery({
    queryKey: queryKeys.communications.stats(userId),
    queryFn: () => communicationsAPI.getCommunicationStats(userId),
    enabled: !!userId,
    retry: false,
  });
};

export const useRetryCommunication = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => communicationsAPI.retryCommunication(id),
    onSuccess: () => {
      toast({
        title: "Communication retried",
        description: "The communication has been retried successfully.",
      });
      // Invalidate all communication queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["communications"] });
    },
    onError: (error: any) => {
      toast({
        title: "Retry failed",
        description:
          error?.response?.data?.error || "Failed to retry communication.",
        variant: "destructive",
      });
    },
  });
};
